import type { InsuranceType } from '~/@types/insurance';
import type { CredsystemStackParamList } from '../credsystem/navigation/types';
import { ScreensRequiredToLogin as ScreensRequiredToLoginCredSystem } from '../credsystem/navigation/types';
import { MarisaAppUtils } from '~/utils/MarisaAppUtils';

interface DefaultHiredInsuranceRouteParams {
  id: string;
}

export type RootStackParamList = {
  MarisaServicos_Home: undefined;
  MarisaServicos_Iframe: { url: string };
  MarisaServicos_ContractSellerInfo: { type: InsuranceType };
  MarisaServicos_ContractSellerConfirmation: { type: InsuranceType };
  MarisaServicos_AboutMarisaCard: undefined;
  MarisaServicos_MarisaTakesCareDetails: undefined;
  MarisaServicos_MarisaTakesCareInsurance: undefined;
  MarisaServicos_MarisaTakesCareContract: {
    step?: number;
  };
  MarisaServicos_BagProtectionDetails: undefined;
  MarisaServicos_PersonalLoan: undefined;
  MarisaServicos_BagProtectionContract: {
    step?: number;
  };
  MarisaServicos_IncomeProtectionDetails: undefined;
  MarisaServicos_IncomeProtectionContract: {
    step?: number;
  };
  MarisaServicos_Components: undefined;
  MarisaServicos_Checkout: {
    step?: number;
    type?: InsuranceType;
    paymentMethods?: Checkout.PaymentMethod[];
  };
  MarisaServicos_MarisaAutoContract: {
    step?: number;
    hasInsurance?: boolean;
    question?: string;
  };
  MarisaServicos_CheckoutSuccess: {
    insuranceType: InsuranceType;
  };
  MarisaServicos_CheckoutError: undefined;
  MarisaServicos_SignIn: undefined;
  MarisaServicos_CreateAccount: {
    step?: number;
  };
  MarisaServicos_ForgotPassword: undefined;
  MarisaServicos_TermsAndConditions: undefined;
  MarisaServicos_NoAuthContractPage: {
    type: InsuranceType;
    navigateTo: string;
  };
  MarisaServicos_HiredInsuranceDetailsAuto: DefaultHiredInsuranceRouteParams;
  MarisaServicos_HiredInsuranceCancelAuto: undefined;
  MarisaServicos_HiredInsuranceCoverageAuto: DefaultHiredInsuranceRouteParams;
  MarisaServicos_HiredInsuranceSinisterAuto: DefaultHiredInsuranceRouteParams;
  MarisaServicos_HiredInsuranceDetailsTakesCare: DefaultHiredInsuranceRouteParams;
  MarisaServicos_HiredInsuranceCancelTakesCare: DefaultHiredInsuranceRouteParams;
  MarisaServicos_HiredInsuranceCancelSuccessTakesCare: undefined;
  MarisaServicos_HiredInsuranceCancelFailTakesCare: DefaultHiredInsuranceRouteParams;
  MarisaServicos_HiredInsuranceCoverageTakesCare: DefaultHiredInsuranceRouteParams;
  MarisaServicos_HiredInsuranceCallCenterTakesCare: DefaultHiredInsuranceRouteParams;
  MarisaServicos_HiredInsuranceDetailsBagProtection: DefaultHiredInsuranceRouteParams;
  MarisaServicos_HiredInsuranceCancelBagProtection: DefaultHiredInsuranceRouteParams;
  MarisaServicos_HiredInsuranceCancelSuccessBagProtection: undefined;
  MarisaServicos_HiredInsuranceCancelFailBagProtection: DefaultHiredInsuranceRouteParams;
  MarisaServicos_HiredInsuranceCoverageBagProtection: DefaultHiredInsuranceRouteParams;
  MarisaServicos_HiredInsuranceCallCenterBagProtection: DefaultHiredInsuranceRouteParams;
  MarisaServicos_HiredInsuranceDetailsIncomeProtection: DefaultHiredInsuranceRouteParams;
  MarisaServicos_HiredInsuranceCancelIncomeProtection: DefaultHiredInsuranceRouteParams;
  MarisaServicos_HiredInsuranceCancelSuccessIncomeProtection: undefined;
  MarisaServicos_HiredInsuranceCancelFailIncomeProtection: DefaultHiredInsuranceRouteParams;
  MarisaServicos_HiredInsuranceCoverageIncomeProtection: DefaultHiredInsuranceRouteParams;
  MarisaServicos_HiredInsuranceCallCenterIncomeProtection: DefaultHiredInsuranceRouteParams;
  MarisaServicos_UnemploymentAssistanceDetails: undefined;
  MarisaServicos_OdontoProtectionDetails: undefined;
  MarisaServicos_MarisaAutoDetails: undefined;
} & CredsystemStackParamList;

type ScreensRequiredToLoginType = {
  [RouteName in keyof RootStackParamList]: boolean;
};

export const ScreensRequiredToLogin: ScreensRequiredToLoginType = {
  MarisaServicos_Home: false,
  MarisaServicos_Iframe: false,
  MarisaServicos_ContractSellerInfo: false,
  MarisaServicos_ContractSellerConfirmation: false,
  MarisaServicos_AboutMarisaCard: false,
  MarisaServicos_MarisaTakesCareDetails: false,
  MarisaServicos_MarisaTakesCareInsurance: false,
  MarisaServicos_BagProtectionDetails: false,
  MarisaServicos_PersonalLoan: false,
  MarisaServicos_BagProtectionContract: true,
  MarisaServicos_IncomeProtectionDetails: false,
  MarisaServicos_IncomeProtectionContract: true,
  MarisaServicos_Components: false,
  MarisaServicos_MarisaTakesCareContract: true,
  MarisaServicos_Checkout: true,
  MarisaServicos_CheckoutSuccess: true,
  MarisaServicos_CheckoutError: true,
  MarisaServicos_SignIn: false,
  MarisaServicos_MarisaAutoContract: true,
  MarisaServicos_CreateAccount: false,
  MarisaServicos_ForgotPassword: false,
  MarisaServicos_TermsAndConditions: false,
  MarisaServicos_NoAuthContractPage: false,
  MarisaServicos_HiredInsuranceDetailsAuto: true,
  MarisaServicos_HiredInsuranceCancelAuto: true,
  MarisaServicos_HiredInsuranceCoverageAuto: true,
  MarisaServicos_HiredInsuranceSinisterAuto: true,
  MarisaServicos_HiredInsuranceDetailsTakesCare: true,
  MarisaServicos_HiredInsuranceCancelTakesCare: true,
  MarisaServicos_HiredInsuranceCancelSuccessTakesCare: true,
  MarisaServicos_HiredInsuranceCancelFailTakesCare: true,
  MarisaServicos_HiredInsuranceCoverageTakesCare: true,
  MarisaServicos_HiredInsuranceCallCenterTakesCare: true,
  MarisaServicos_HiredInsuranceDetailsBagProtection: true,
  MarisaServicos_HiredInsuranceCancelBagProtection: true,
  MarisaServicos_HiredInsuranceCancelSuccessBagProtection: true,
  MarisaServicos_HiredInsuranceCancelFailBagProtection: true,
  MarisaServicos_HiredInsuranceCoverageBagProtection: true,
  MarisaServicos_HiredInsuranceCallCenterBagProtection: true,
  MarisaServicos_HiredInsuranceDetailsIncomeProtection: true,
  MarisaServicos_HiredInsuranceCancelIncomeProtection: true,
  MarisaServicos_HiredInsuranceCancelSuccessIncomeProtection: true,
  MarisaServicos_HiredInsuranceCancelFailIncomeProtection: true,
  MarisaServicos_HiredInsuranceCoverageIncomeProtection: true,
  MarisaServicos_HiredInsuranceCallCenterIncomeProtection: true,
  MarisaServicos_UnemploymentAssistanceDetails: false,
  MarisaServicos_OdontoProtectionDetails: false,
  MarisaServicos_MarisaAutoDetails: false,
  ...ScreensRequiredToLoginCredSystem,
};

export const getRequiredScreensToLogin = () => {
  const contractRoutes = {
    MarisaServicos_BagProtectionContract: false,
    MarisaServicos_IncomeProtectionContract: false,
    MarisaServicos_MarisaTakesCareContract: false,
    MarisaServicos_MarisaAutoContract: false,
    MarisaServicos_Checkout: false,
    MarisaServicos_CheckoutSuccess: false,
    MarisaServicos_CheckoutError: false,
  };

  if (['totem', 'crediario'].includes(MarisaAppUtils.origin)) {
    return { ...ScreensRequiredToLogin, ...contractRoutes };
  }

  return ScreensRequiredToLogin;
};

export {};
